import {useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import Analytics from 'utils/Analytics';
import {useGlp1Modality} from 'surveys/osa_funnel/hooks/useGlp1Modality';
import {modalityToVariant} from '../paywall/paywallCopy';
import styles from './Glp1SoldOutModal.module.css';
import riseExpertCareLogo from 'surveys/osa_funnel/assets/rise-expert-care-logo.svg';

const RefundIcon = () => (
  <svg width={18} height={18} viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.7}>
    <path d="M2.5 9 a 6.5 6.5 0 1 0 1.7 -4.4" strokeLinecap="round" />
    <path d="M2.5 2.5 v 4 h 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ThumbIcon = () => (
  <svg width={18} height={18} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path
      d="M5.5 8.5 V 18 H 14.2 a 1.5 1.5 0 0 0 1.5 -1.2 l 1.4 -6.4 a 1.5 1.5 0 0 0 -1.5 -1.8 H 11.5 V 5 a 2 2 0 0 0 -3.6 -1.2 L 5.5 8.5 Z"
      strokeLinejoin="round"
    />
    <path d="M2 9 H 5.5 V 18 H 2 Z" strokeLinejoin="round" />
  </svg>
);

const SunIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
  >
    <circle cx={9} cy={9} r={3.4} />
    <path d="M9 1.6 V 3.4 M9 14.6 V 16.4 M1.6 9 H 3.4 M14.6 9 H 16.4 M3.6 3.6 L 4.9 4.9 M13.1 13.1 L 14.4 14.4 M14.4 3.6 L 13.1 4.9 M4.9 13.1 L 3.6 14.4" />
  </svg>
);

type Props = {
  isVisible: boolean;
  onContinue: () => void;
};

/** "RISE Expert Care is full right now" — terminal fake-door modal.
 *  Fades in after the medical screening submits. CTA exits the user
 *  out of the OSA funnel. */
export const Glp1SoldOutModal = ({isVisible, onContinue}: Props) => {
  const modality = useGlp1Modality();
  const variant = modalityToVariant(modality);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    Analytics.track('GLP-1 Funnel Sold Out Viewed', {variant});
    // rAF so the backdrop element mounts at opacity 0 before the
    // transition runs, otherwise the visible class is applied in the
    // same paint and the fade is skipped.
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, [isVisible, variant]);

  useEffect(() => {
    if (!isVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const handleContinue = () => {
    // The download-tap analytics + app-store redirect live in the
    // parent's onContinue (it owns the device-aware destination).
    onContinue();
  };

  return createPortal(
    <div className={`${styles.backdrop} ${visible ? styles.backdropVisible : ''}`} role="dialog" aria-modal="true">
      <div className={styles.card}>
        <div className={styles.cardContent}>
          <div className={styles.logoWrap}>
            <img src={riseExpertCareLogo} alt="RISE Expert Care" className={styles.logo} />
          </div>

          <h1 className={styles.heading}>
            RISE Expert Care is <span className={styles.headingAccent}>full right now.</span>
          </h1>

          <p className={styles.subhead}>
            We&apos;ve had higher demand than expected. New prescriptions are paused while we expand capacity. Your
            refund has been issued.
          </p>

          <div className={styles.rows}>
            <div className={styles.row}>
              <span className={`${styles.iconCircle} ${styles.iconRefund}`}>
                <RefundIcon />
              </span>
              <div className={styles.rowText}>
                <span className={`${styles.rowTitle} ${styles.rowTitleRefund}`}>Your refund has been issued.</span>
                <p className={styles.rowBody}>You&apos;ll see it back on your card within 5–7 days.</p>
              </div>
            </div>

            <div className={styles.row}>
              <span className={`${styles.iconCircle} ${styles.iconWaitlist}`}>
                <ThumbIcon />
              </span>
              <div className={styles.rowText}>
                <span className={`${styles.rowTitle} ${styles.rowTitleWaitlist}`}>You&apos;re on the list.</span>
                <p className={styles.rowBody}>We&apos;ll email you when RISE Expert Care opens up.</p>
              </div>
            </div>

            <div className={styles.row}>
              <span className={`${styles.iconCircle} ${styles.iconRise}`}>
                <SunIcon />
              </span>
              <div className={styles.rowText}>
                <span className={`${styles.rowTitle} ${styles.rowTitleRise}`}>Two months of RISE, on us.</span>
                <p className={styles.rowBody}>
                  For the trouble — full RISE access, free for 60 days. No charge, no card required.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.ctaWrap}>
          <button type="button" className={styles.cta} onClick={handleContinue}>
            Download RISE
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Glp1SoldOutModal;
